import type { RfqType } from "@convex/constants/types";
import { RFQ_FORM_TYPES } from "@convex/constants/leads";
import { getCanonicalConditionValue } from "@shared/generated/productConditions";
import { isOneOf } from "@shared/type-guards";
import { builderHandoffFromParams, postalCodeFromParams } from "@/lib/rfq/builder-handoff";
import type { RfqCreateArgs, RfqLocationInput } from "../types";
import { normalizeRfqRegion } from "../rfq-location-validation";
import { requestQuoteToolInputSchema, type RequestQuoteToolInput } from "./request-quote-tool";

export const REQUEST_QUOTE_PREFILL_FIELDS = [
  "product",
  "condition",
  "qty",
  "city",
  "state",
  "zip",
] as const;

export type RequestQuotePrefillField = (typeof REQUEST_QUOTE_PREFILL_FIELDS)[number];

export interface RequestQuotePrefill {
  productType?: RequestQuoteToolInput["productType"];
  condition?: string;
  quantity?: number;
  location?: Partial<RfqLocationInput["address"]>;
  fields: RequestQuotePrefillField[];
}

export interface QuoteEntryProps {
  initialType?: RfqType;
  initialProductSlug?: string;
  initialQuantity?: number;
  initialLocation?: Partial<RfqLocationInput["address"]>;
  initialFormValues?: Partial<RfqCreateArgs>;
  prefilledFields?: RequestQuotePrefillField[];
}

const WHOLE_NUMBER = /^\d+$/;
const { productType, quantity, delivery } = requestQuoteToolInputSchema.shape;

function firstValues(searchParams: URLSearchParams): Record<string, string> {
  const values: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (!(key in values)) values[key] = value;
  });
  return values;
}

function parseProductType(value: string | undefined) {
  if (value === undefined) return undefined;
  const result = productType.safeParse(value);
  return result.success ? result.data : undefined;
}

function parseQuantity(value: string | undefined): number | undefined {
  const trimmed = value?.trim();
  if (!trimmed || !WHOLE_NUMBER.test(trimmed)) return undefined;
  const result = quantity.safeParse(Number(trimmed));
  return result.success ? result.data : undefined;
}

function parseCity(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const result = delivery.shape.city.safeParse(value);
  return result.success ? result.data : undefined;
}

export function parseRequestQuotePrefill(searchParams: URLSearchParams): RequestQuotePrefill {
  const params = firstValues(searchParams);
  const product = parseProductType(params.product);
  const condition =
    product && params.condition
      ? getCanonicalConditionValue(product, params.condition)
      : undefined;
  const units = parseQuantity(params.qty);
  const city = parseCity(params.city);
  const region = normalizeRfqRegion(params.state);
  const postalCode = postalCodeFromParams(params);
  const accepted: Record<RequestQuotePrefillField, boolean> = {
    product: product !== undefined,
    condition: condition !== undefined,
    qty: units !== undefined,
    city: city !== undefined,
    state: region !== undefined,
    zip: postalCode !== undefined,
  };
  return {
    productType: product,
    condition,
    quantity: units,
    location: region || postalCode || city ? { ...region, city, postalCode } : undefined,
    fields: REQUEST_QUOTE_PREFILL_FIELDS.filter((field) => accepted[field]),
  };
}

function withPrefilledCondition(
  handoff: Partial<RfqCreateArgs> | undefined,
  prefill: RequestQuotePrefill,
): Partial<RfqCreateArgs> | undefined {
  const values: Partial<RfqCreateArgs> = {
    ...handoff,
    ...(prefill.condition ? { condition: prefill.condition } : {}),
  };
  if (values.condition && prefill.productType) {
    const canonical = getCanonicalConditionValue(prefill.productType, values.condition);
    if (canonical) values.condition = canonical;
    else delete values.condition;
  }
  return Object.keys(values).length > 0 ? values : undefined;
}

export function quoteEntryFromSearchParams(searchParams: URLSearchParams): QuoteEntryProps {
  const params = firstValues(searchParams);
  const prefill = parseRequestQuotePrefill(searchParams);
  return {
    initialType: isOneOf(RFQ_FORM_TYPES, params.type) ? params.type : undefined,
    initialProductSlug: prefill.productType,
    initialQuantity: prefill.quantity,
    initialLocation: prefill.location,
    initialFormValues: withPrefilledCondition(builderHandoffFromParams(params), prefill),
    prefilledFields: prefill.fields,
  };
}
